import React from 'react';
import { Card, Button, Space, Typography } from 'antd';
import { PhoneOutlined, MessageOutlined, ClockCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/userSlice';
import { useNavigate } from 'react-router-dom';
import './PendingApproval.css';

const { Title, Paragraph } = Typography;

const PendingApproval = ({ user }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const isRejected = user.approval_status === 'rejected';
  const isPending = user.approval_status === 'pending';
  
  const whatsappMessage = isRejected 
    ? `My account was rejected - Name: ${user.first_name} ${user.last_name} Phone: ${user.phone_number} Email: ${user.email} Reason: ${user.rejection_reason || 'No reason provided'}`
    : `I want to approve my account - Name: ${user.first_name} ${user.last_name} Phone: ${user.phone_number} Email: ${user.email}`;
  const whatsappUrl = `https://wa.me/00923281419656?text=${encodeURIComponent(whatsappMessage)}`;

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="pending-approval-container">
      <div className="pending-approval-content">
        {/* Logo Section */}
        <div className="logo-section">
          <img 
            src="/assets/images/logo.png" 
            alt="MaveshiBids" 
            className="logo-image"
            onClick={() => navigate('/')}
          />
        </div>

        <div className="pending-approval-header">
          <ClockCircleOutlined className={`status-icon ${isRejected ? 'rejected' : 'pending'}`} />
          <Title level={2} className="pending-approval-header">
            {isRejected ? 'Account Rejected' : 'Account Pending Approval'}
          </Title>
          <Paragraph className="pending-approval-header">
            {isRejected 
              ? 'Your account has been rejected by an administrator. Please see the reason below and contact us if you have any questions.'
              : 'Your account has been created successfully and is currently pending admin approval. You\'ll receive an email notification once your account is approved.'
            }
          </Paragraph>
        </div>

        {/* Rejection Reason Section - Only show for rejected accounts */}
        {isRejected && user.rejection_reason && (
          <div className="rejection-reason-section">
            <Title level={4} className="rejection-reason-section">
              Rejection Reason
            </Title>
            <Paragraph className="rejection-reason-text">
              {user.rejection_reason}
            </Paragraph>
          </div>
        )}

        <div className={`contact-section ${isRejected ? 'rejected' : 'pending'}`}>
          <Title level={4} className={`contact-section ${isRejected ? 'rejected' : 'pending'}`}>
            {isRejected ? 'Need Help?' : 'For Faster Approval'}
          </Title>
          <Paragraph className={`contact-section ${isRejected ? 'rejected' : 'pending'}`}>
            {isRejected 
              ? 'If you believe this rejection was made in error or you have additional information to provide, please contact us:'
              : 'Contact us directly with your account details:'
            }
          </Paragraph>
          
          <div className="user-details">
            <strong>Name:</strong> {user.first_name} {user.last_name}<br />
            <strong>Phone:</strong> {user.phone_number}<br />
            <strong>Email:</strong> {user.email}
          </div>
        </div>

        <div className="contact-buttons">
          <Button
            type="primary"
            size="large"
            icon={<PhoneOutlined />}
            href="tel:0000923281419656"
            className="contact-button call"
          >
            📞 Call Us: 0000923281419656
          </Button>
          
          <Button
            size="large"
            icon={<MessageOutlined />}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-button whatsapp"
          >
            💬 {isRejected ? 'Contact Support' : 'WhatsApp Us'}
          </Button>
        </div>

        <div className="note-section">
          <Paragraph className="note-section">
            <strong>Note:</strong> {isRejected 
              ? 'Your account has been rejected. Please contact support if you believe this was an error or if you have additional information to provide.'
              : 'You won\'t be able to access the application features until your account is approved by an administrator.'
            }
          </Paragraph>
        </div>

        <Button
          type="default"
          size="large"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          className="logout-button"
        >
          Logout
        </Button>
      </div>
    </div>
  );
};

export default PendingApproval;
